import { useQuery } from '@apollo/client';
import FinancialCalendars from '../../graphql/queries/company/FinancialCalendarsQuery.graphql';
import {
  FilterOperation,
  IFinancialCalendar,
  IFinancialCalendarsQueryResult,
  IFinancialCalendarsQueryVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useMemo } from 'react';
import { addYears, format } from 'date-fns';
import { useCompany } from '../FetchConfig';

/**
 * Hook to return the SPECIFIED financial calendar records ONLY.
 */
export const useCurrentFinancialYear = (): Maybe<IFinancialCalendar> => {
  const company = useCompany();
  const now = new Date().toISOString().slice(0, 10);

  const { data: financialCalendarsData } = useQuery<IFinancialCalendarsQueryResult, IFinancialCalendarsQueryVariables>(
    FinancialCalendars,
    {
      variables: {
        orderBy: 'year',
        filters: [
          {
            filterOperation: FilterOperation.LessThanEqual,
            filterParameters: {
              field: 'yearStartDate',
              value: now,
            },
          },
          {
            filterOperation: FilterOperation.GreaterThanEqual,
            filterParameters: {
              field: 'yearEndDate',
              value: now,
            },
          },
        ],
      },
      skip: !company?.currentCalendarYearEnabled,
    }
  );
  return financialCalendarsData?.listData?.edges?.[0]?.node;
};

/**
 * Hook to return the CURRENT and PREVIOUS financial calendar records together WHERE AVAILABLE.
 */
export const useSpecificAndPreviousFinancialYear = (
  currentYear: number,
  previousYear: number
): {
  current?: IFinancialCalendar;
  previous?: IFinancialCalendar;
} => {
  const yearFilter = `${currentYear.toString()}, ${previousYear.toString()}`;

  const { data: financialCalendarsData } = useQuery<IFinancialCalendarsQueryResult, IFinancialCalendarsQueryVariables>(
    FinancialCalendars,
    {
      variables: {
        orderBy: 'year',
        filters: [
          {
            filterOperation: FilterOperation.In,
            filterParameters: {
              field: 'year',
              value: yearFilter,
            },
          },
        ],
      },
    }
  );

  return useMemo(() => {
    const list = financialCalendarsData?.listData?.edges?.map((edge) => normalizeVariables(edge!.node)) ?? [];
    if (list.length > 0) {
      return { current: list[1], previous: list[0] };
    }
    return { current: null, previous: null };
  }, [financialCalendarsData?.listData?.edges]);
};

/**
 * Retrieve the financial calendars sorted by year
 */
export const useFinancialCalendars = (includeFutureYears: boolean) => {
  const { data: financialCalendarsData } = useQuery<IFinancialCalendarsQueryResult, IFinancialCalendarsQueryVariables>(
    FinancialCalendars,
    {
      variables: {
        orderBy: 'year',
        filters: includeFutureYears
          ? []
          : [
              {
                filterOperation: FilterOperation.LessThan,
                filterParameters: {
                  field: 'yearStartDate',
                  value: format(addYears(new Date(), 1), 'yyyy-MM-dd'),
                },
              },
            ],
      },
    }
  );

  return useMemo<Array<Maybe<{ __typename?: 'FinancialCalendar' } & IFinancialCalendarFragment>>>(() => {
    if (financialCalendarsData?.listData?.edges) {
      return financialCalendarsData.listData.edges.map((edge) => {
        return normalizeVariables(edge?.node);
      });
    }
    return [] as Array<Maybe<{ __typename?: 'FinancialCalendar' } & IFinancialCalendarFragment>>;
  }, [financialCalendarsData]);
};
